import React, { useState, useCallback, useRef, useEffect } from "react";
import { Rnd } from "react-rnd";

import {
  getIsMultiOrgWorkflowsEnabled,
  getIsWorkflowsEnabled,
} from "Utils/ACLStoreUtils";
import AttachmentLoaderMenu from "Attachment/AttachmentLoaderMenu";
import WorkflowProgressMenu from "AppComponents/WorkflowProgressMenu";
import IntraOrgWorkflowProgressMenu from "AppComponents/IntraOrgWorkflowProgressMenu";
import { useI18n } from "Hooks";

import classes from "./GlobalToastWrapper.scss";

const rndDefaultStyle = {
  x: 24,
  y: window.innerHeight - 120,
  width: "auto",
  height: "auto",
};

const GlobalToastWrapper = () => {
  const [position, setPosition] = useState(rndDefaultStyle);
  const rndRef = useRef(null);
  const wrapperRef = useRef(null);
  const previousHeightRef = useRef(null);
  const hasDraggedRef = useRef(false);
  const { t } = useI18n(["common"]);

  const shouldShowWorkflowProgressMenu = getIsMultiOrgWorkflowsEnabled();
  const shouldShowIntraOrgWorkflowProgressMenu = getIsWorkflowsEnabled();

  useEffect(() => {
    if (!wrapperRef.current) return;

    const handleResize = entries => {
      for (const entry of entries) {
        const { height } = entry.contentRect;

        if (
          previousHeightRef.current !== null &&
          previousHeightRef.current !== height
        ) {
          const heightDifference = height - previousHeightRef.current;

          setPosition(prevPosition => {
            const newY = prevPosition.y - heightDifference;
            return {
              ...prevPosition,
              y: Math.max(0, Math.min(newY, window.innerHeight - height - 16)),
            };
          });
        }

        previousHeightRef.current = height;
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(wrapperRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleWindowResize = () => {
      // Reset to original position on window resize
      setPosition({
        x: 24,
        y: window.innerHeight - 120,
        width: "auto",
        height: "auto",
      });
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  const handleDragStop = useCallback(() => {
    setTimeout(() => {
      hasDraggedRef.current = false;
    }, 100);
  }, []);

  const handlePositionChange = useCallback(
    (event, data) => {
      hasDraggedRef.current = true;
      setPosition({ ...position, x: data.x, y: data.y });
    },
    [position]
  );

  const handleClick = useCallback(event => {
    if (hasDraggedRef.current) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  const renderWorkflowMenu = () => {
    if (shouldShowWorkflowProgressMenu) {
      return <WorkflowProgressMenu />;
    }
    if (shouldShowIntraOrgWorkflowProgressMenu) {
      return <IntraOrgWorkflowProgressMenu />;
    }
    return null;
  };

  return (
    <Rnd
      ref={rndRef}
      position={{ x: position.x, y: position.y }}
      size={{ width: position.width, height: position.height }}
      enableResizing={false}
      bounds="window"
      className={classes.rndWrapper}
      onDragStop={handleDragStop}
      onDrag={handlePositionChange}
    >
      <div
        ref={wrapperRef}
        className={classes.toastWrapper}
        onClickCapture={handleClick}
        onMouseDownCapture={handleClick}
        onKeyDownCapture={handleClick}
        role="button"
        tabIndex={0}
        aria-label={t("common:task_progress_details")}
      >
        {renderWorkflowMenu()}
        <AttachmentLoaderMenu />
      </div>
    </Rnd>
  );
};

export default GlobalToastWrapper;
